import { FC, memo, RefObject, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { SIDE } from "_constants";
import { MatchStatus } from "service";
import { getTeamColors } from "components/colors";
import { Text } from "./Text";

type BallBaseProps = {
  status: MatchStatus;
  ballImageHref: string;
  viewW?: number;
  viewH?: number;
  ballSize?: number;
  pathLineMax?: number;
  pathStopperEvents?: string[];
  showShadowEllipse?: boolean;
  extraPathDeps?: any[];
  ballExtraOffsetYPx?: number;
  shadowOffsetYPx?: number;
  smoothMs?: number;
  hidden?: boolean;
  ballElementRef?: RefObject<SVGImageElement | null>;
  label?: string;
  opponentLabel?: {
    text: string;
    side: SIDE;
    x?: number;
    y?: number;
    anchor?: "ball" | "edge";
  };
};

export const BallBase: FC<BallBaseProps> = memo(
  ({
    status,
    ballImageHref,
    viewW = 400,
    viewH = 180,
    ballSize = 12,
    pathLineMax = 5,
    pathStopperEvents = [],
    showShadowEllipse = true,
    extraPathDeps = [],
    ballExtraOffsetYPx = 0,
    shadowOffsetYPx = 3,
    smoothMs = 400,
    hidden = false,
    ballElementRef,
    label,
    opponentLabel,
  }) => {
    const { x, y, nsc, side, stn } = (status || {}) as any;

    const [{ path }, setState] = useState<{ path: { x: number; y: number }[] }>(
      { path: [] }
    );

    const pXRef = useRef<number | undefined>(undefined);
    const pYRef = useRef<number | undefined>(undefined);

    // animated coords for smooth pass motion
    const [{ ax, ay }, setAnim] = useState<{ ax: number; ay: number }>({
      ax: typeof x === "number" ? x : 0.5,
      ay: typeof y === "number" ? y : 0.5,
    });
    const rafRef = useRef<number | null>(null);
    const startRef = useRef<number | null>(null);

    const easeOutQuad = (t: number) => 1 - (1 - t) * (1 - t);

    useEffect(() => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      startRef.current = null;

      if (typeof x !== "number" || typeof y !== "number") {
        return;
      }

      const fromX = ax;
      const fromY = ay;
      const toX = x;
      const toY = y;

      const step = (ts: number) => {
        if (startRef.current === null) startRef.current = ts;
        const elapsed = ts - (startRef.current ?? 0);
        const d =
          smoothMs > 0 ? Math.max(0, Math.min(1, elapsed / smoothMs)) : 1;
        const t = smoothMs > 0 ? easeOutQuad(d) : 1;
        const nx = fromX + (toX - fromX) * t;
        const ny = fromY + (toY - fromY) * t;
        setAnim({ ax: nx, ay: ny });
        if (d < 1) {
          rafRef.current = requestAnimationFrame(step);
        } else {
          rafRef.current = null;
        }
      };

      rafRef.current = requestAnimationFrame(step);
      return () => {
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [x, y, smoothMs]);

    useEffect(() => {
      if (typeof x !== "number" || typeof y !== "number") {
        setState((s) => ({ ...s, path: [] }));
        return;
      }
      let addToPath = true;
      if (x === pXRef.current && y === pYRef.current) addToPath = false;
      pXRef.current = x;
      pYRef.current = y;

      setState((s) => ({
        ...s,
        path: addToPath
          ? [...s.path.slice(-pathLineMax + 1), { x, y }]
          : s.path,
      }));
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [x, y, ...extraPathDeps]);

    const sideRef = useRef<SIDE | undefined>(SIDE.NO_SIDE);
    useEffect(() => {
      if (sideRef.current !== side) {
        setState((s) => ({
          ...s,
          path: s.path.length > 0 ? s.path.slice(-1) : [],
        }));
        sideRef.current = side;
      }
    }, [side]);

    useEffect(() => {
      if (nsc && pathStopperEvents.includes(nsc))
        setState((s) => ({ ...s, path: [] }));
    }, [nsc, pathStopperEvents]);

    if (typeof x !== "number" || typeof y !== "number") return <></>;

    const trimmedLabel =
      typeof label === "string" && label.trim().length > 0
        ? label.trim()
        : null;
    let playerFromStatus: string | null = null;
    if (typeof stn === "string" && stn.includes("|")) {
      const [, rawName] = stn.split("|");
      if (typeof rawName === "string") {
        const trimmedName = rawName.trim();
        if (trimmedName.length > 0) {
          playerFromStatus = trimmedName;
        }
      }
    }

    const playerName = trimmedLabel ?? playerFromStatus;

    const { home, away } = getTeamColors(status);
    const teamColor = side === SIDE.HOME ? home : away;

    const normalizedOpponentLabel =
      typeof opponentLabel?.text === "string" &&
      opponentLabel.text.trim().length > 0
        ? opponentLabel.text.trim()
        : null;
    const opponentColor =
      opponentLabel?.side === SIDE.AWAY
        ? away
        : opponentLabel?.side === SIDE.HOME
        ? home
        : teamColor;
    const opponentAnchor = opponentLabel?.anchor === "edge" ? "edge" : "ball";

    const resolveNormalized = (value: number | undefined, fallback: number) => {
      if (typeof value !== "number") return fallback;
      if (Number.isNaN(value)) return fallback;
      return Math.max(0, Math.min(1, value));
    };

    const opponentX = resolveNormalized(
      opponentLabel?.x,
      opponentAnchor === "edge"
        ? opponentLabel?.side === SIDE.HOME
          ? 0
          : 1
        : opponentLabel?.side === SIDE.HOME
        ? 0.07
        : 0.93
    );
    const opponentY = resolveNormalized(
      opponentLabel?.y,
      typeof ay === "number" ? ay : 0.5
    );

    const HALF = ballSize / 2;

    // clamp so full icon stays inside
    const cx = Math.max(HALF, Math.min(viewW - HALF, (ax || 0) * viewW));
    const cy = Math.max(HALF, Math.min(viewH - HALF, (ay || 0) * viewH));
    const centerY = cy + (ballExtraOffsetYPx || 0);

    return (
      <BallWrapper>
        <PathContainer>
          <svg width="100%" height="100%" viewBox={`0 0 ${viewW} ${viewH}`}>
            <polyline
              points={path
                .slice(0, -1)
                .map(({ x, y }) => `${x * viewW},${y * viewH}`)
                .join(" ")}
              stroke={"white"}
              strokeWidth="2px"
              fill="none"
              strokeDasharray="5,5"
            />

            <polyline
              points={path
                .slice(-2)
                .map(({ x, y }) => `${x * viewW},${y * viewH}`)
                .join(" ")}
              stroke={"white"}
              strokeWidth="2px"
              fill="none"
              strokeDasharray="none"
            />

            {showShadowEllipse && (
              <ellipse
                cx={ax * viewW}
                cy={ay * viewH + shadowOffsetYPx}
                rx="6vw"
                ry="3vw"
                fill={teamColor}
                strokeWidth="1px"
                stroke={teamColor}
                style={{ perspective: "800px", transformStyle: "preserve-3d" }}
              >
                <animate
                  attributeName="rx"
                  begin="0s"
                  dur="1s"
                  repeatCount="indefinite"
                  from="6px"
                  to="9px"
                />
                <animate
                  attributeName="ry"
                  begin="0s"
                  dur="1s"
                  repeatCount="indefinite"
                  from="5px"
                  to="7px"
                />
                <animate
                  attributeName="opacity"
                  begin="0s"
                  dur="1s"
                  repeatCount="indefinite"
                  from="1"
                  to="0.3"
                />
              </ellipse>
            )}

            {path.slice(-pathLineMax).map((p, i, array) => (
              <circle
                r="3px"
                cx={p.x * viewW}
                cy={p.y * viewH}
                key={i}
                fill="transparent"
                stroke={"transparent"}
                opacity={
                  (array.length === pathLineMax && array.length - 1) === i
                    ? "0.2"
                    : "1"
                }
              />
            ))}

            {path.length >= pathLineMax + 1 && (
              <circle
                r="3px"
                cx={path[path.length - pathLineMax - 1].x * viewW}
                cy={path[path.length - pathLineMax - 1].y * viewH}
                stroke={"white"}
                fill="white"
                opacity="0.2"
                style={{ opacity: 0.3 }}
              />
            )}

            {!hidden && (
              <StyledBall
                href={ballImageHref}
                x={cx - HALF}
                y={centerY - HALF}
                width={ballSize}
                height={ballSize}
                data-center-x={cx}
                data-center-y={centerY}
                data-view-width={viewW}
                data-view-height={viewH}
                ref={ballElementRef ? ballElementRef : undefined}
              />
            )}
          </svg>
        </PathContainer>

        {playerName && !hidden && (
          <PlayerNameBox
            side={side ?? SIDE.HOME}
            x={ax}
            y={ay}
            bgColor={teamColor}
            anchor="ball"
          >
            <Text variant="playerNameBall" className="text-outline">
              {playerName}
            </Text>
          </PlayerNameBox>
        )}

        {normalizedOpponentLabel && !hidden && (
          <PlayerNameBox
            side={opponentLabel?.side ?? SIDE.NO_SIDE}
            x={opponentX}
            y={opponentY}
            bgColor={opponentColor}
            anchor={opponentAnchor}
          >
            <Text variant="playerNameBall" className="text-outline">
              {normalizedOpponentLabel}
            </Text>
          </PlayerNameBox>
        )}
      </BallWrapper>
    );
  }
);

const clampPercent = (value: number, min = 4, max = 96) =>
  Math.max(min, Math.min(max, value));

const getPlayerNameTop = (y: number, anchor?: "ball" | "edge"): number => {
  if (anchor === "edge") {
    return clampPercent(y * 100, 4, 96);
  }
  const verticalOffset = y < 0.1 ? 8 : -8;
  return clampPercent(y * 100 + verticalOffset, 4, 96);
};

const getPlayerNameLeft = (x: number, anchor?: "ball" | "edge"): string => {
  if (anchor === "edge") {
    return x <= 0.5 ? "0%" : "100%";
  }
  if (x < 0.08) return "8%";
  if (x > 0.92) return "92%";
  return `${x * 100}%`;
};

const getPlayerNameTransform = (
  x: number,
  y: number,
  anchor?: "ball" | "edge"
): string => {
  if (anchor === "edge") {
    return x <= 0.5 ? "translate(6%, -50%)" : "translate(-106%, -50%)";
  }
  if (y < 0.1) return "translate(-50%, 105%)";
  if (x < 0.08) return "translate(100%, -50%)";
  if (x > 0.92) return "translate(-100%, -50%)";
  return "translate(-50%, -110%)";
};

const PathContainer = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  right: 0;
  left: 0;
  transform: none !important;

  & > svg {
    width: 100%;
    height: 100%;
    overflow: visible;
    pointer-events: none;
  }
`;

export const PlayerNameBox = styled.div<{
  side: SIDE;
  x: number;
  y: number;
  bgColor: string;
  anchor?: "ball" | "edge";
}>`
  position: absolute;
  font-weight: 600;
  color: white;
  background-color: ${({ bgColor }) => bgColor};
  padding: 0.45vw 0.9vw;
  border-radius: 0.45vw;
  text-align: center;
  white-space: nowrap;
  pointer-events: none;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  z-index: 99999;
  top: ${({ y, anchor }) => `${getPlayerNameTop(y, anchor)}%`};
  left: ${({ x, anchor }) => getPlayerNameLeft(x, anchor)};
  transform: ${({ x, y, anchor }) => getPlayerNameTransform(x, y, anchor)};
`;

const BallWrapper = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  transform: perspective(-350px) rotateX(50deg) !important;
  transform-style: preserve-3d;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999999999;
  pointer-events: none;
`;

const StyledBall = styled.image`
  position: absolute;
  transform: none !important;
  backface-visibility: hidden;
  will-change: transform;
  z-index: 999999999;
  pointer-events: none;
  overflow: visible;
  clip-path: none;
`;
